import { type AssistantToolCall } from '../../framework/models';
import Agent from './Agent';
import { type WhiteboardController } from '@aha-app/whiteboard';
import type AssistantViewController from '../components/AssistantView/AssistantViewController';
import {
  type AgentSuggestions,
  type AgentParams,
  type AgentInstances,
} from './Agent';

class WhiteboardAgent extends Agent {
  static name = 'WhiteboardAgent';

  static publicName = 'Whiteboard';

  static available(
    _agentParams: AgentParams,
    agentInstances: AgentInstances
  ): boolean {
    return !!(
      window.currentAccount?.hasGeneralAiAssistant &&
      window.featureFlags.awt_390_whiteboard_ai_assistant &&
      agentInstances.getWhiteboardController
    );
  }

  getWhiteboardController: () => WhiteboardController;

  constructor(
    controller: AssistantViewController,
    agentParams: AgentParams,
    agentInstances: AgentInstances
  ) {
    super(controller, agentParams, agentInstances);

    this.getWhiteboardController = agentInstances.getWhiteboardController;
  }

  getSuggestions(): AgentSuggestions {
    return [
      {
        title: 'Help me brainstorm',
        prompt:
          'I need help brainstorming ideas for my project. Can you guide me through a structured brainstorming process?',
        icon: 'fa-map-signs',
      },
      {
        title: 'Add records to whiteboard',
        prompt:
          'I want to add some features and epics to my whiteboard. Can you help me search for and insert relevant records?',
        icon: 'fa-tasks',
      },
      {
        title: 'Summarize my whiteboard',
        prompt:
          'Summarize the current whiteboard content, including key ideas and concepts.',
        icon: 'fa-globe',
      },
      {
        title: 'Collaboration tips',
        prompt:
          'What are some best practices for collaborating effectively on whiteboards?',
        icon: 'fa-pencil-alt',
      },
    ];
  }

  provideToolCallData(toolCall: AssistantToolCall): Object | false {
    if (toolCall.tool === 'WhiteboardAgent::GetCurrentWhiteboardContentTool') {
      return {
        whiteboardData: {
          objects: this.getWhiteboardController().getContentSummary(),
        },
      };
    }

    return false;
  }

  processToolCall(toolCall: AssistantToolCall): void {
    if (toolCall.tool === 'WhiteboardAgent::InsertRecordsTool') {
      if (toolCall.content?.data && typeof toolCall.content.data === 'string') {
        const { record_urls } = JSON.parse(toolCall.content.data) as any;
        this.getWhiteboardController().actionAddAhaRecords(record_urls);
      }
    } else if (toolCall.tool === 'WhiteboardAgent::CreateWireframeTool') {
      if (toolCall.content?.data) {
        const { wireframe } = JSON.parse(toolCall.content.data) as any;
        const controller = this.getWhiteboardController();
        controller.actionAddGeneratedWireframe(wireframe);
      }
    } else if (toolCall.tool === 'WhiteboardAgent::WhiteboardEditTool') {
      // Extract operation data from the tool call
      if (toolCall.content?.data) {
        const { edits } = JSON.parse(toolCall.content.data) as any;

        // Pass the edits to the whiteboard controller
        const controller = this.getWhiteboardController();
        controller.actionProcessOperations(edits);
      }
    }
  }
}

Agent.register(WhiteboardAgent);

export default WhiteboardAgent;
